import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, FileText, Package, Wallet, Receipt, TrendingUp, Landmark,
  BarChart3, Store, ClipboardCheck, History, LogOut, Menu, ChevronDown,
  CalendarPlus, ScrollText, KeyRound, DatabaseBackup, Coffee,
  CalendarCheck, ArrowLeftRight, Users, Banknote, Calculator, ChefHat, Sun, Moon, Lock, FolderOpen, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Select } from "@/components/kit";
import { NotificationBell } from "@/components/NotificationBell";
import api from "@/lib/api";

/* ---------- Grouped nav model (Chutney & Turmeric redesign) ---------- */
const PINNED = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "manager"] },
  { to: "/verification", label: "Verify & Settle", icon: ClipboardCheck, roles: ["admin"], badge: "pending" },
];
const GROUPS = [
  { key: "overview", label: "Overview", items: [
    { to: "/daily", label: "Daily Entry", icon: FileText, roles: ["manager"], badge: "returned" },
    { to: "/history", label: "Entry History", icon: ScrollText, roles: ["admin", "manager"] },
    { to: "/backdate", label: "Back-Date Entry", icon: CalendarPlus, roles: ["admin", "manager"] },
    { to: "/my-report", label: "My Daily Report", icon: FileText, roles: ["manager"] },
  ]},
  { key: "money", label: "Money", items: [
    { to: "/pnl", label: "Profit & Loss", icon: TrendingUp, roles: ["admin"] },
    { to: "/deposits", label: "Cash Pickup", icon: Landmark, roles: ["admin", "manager"] },
    { to: "/advances", label: "Advances", icon: Wallet, roles: ["admin", "manager"] },
    { to: "/bills", label: "Month-End Ledger", icon: Receipt, roles: ["admin"] },
  ]},
  { key: "reports", label: "Reports", items: [
    { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "manager"] },
  ]},
  { key: "people", label: "People", items: [
    { to: "/hr/employees", label: "Employees", icon: Users, roles: ["admin", "manager", "kitchen"] },
    { to: "/hr/attendance", label: "Attendance", icon: CalendarCheck, roles: ["admin", "manager", "kitchen"] },
    { to: "/hr/payroll", label: "Payroll", icon: Calculator, roles: ["admin"] },
    { to: "/hr/loans", label: "Loans & Advances", icon: Banknote, roles: ["admin"] },
    { to: "/hr/workforce", label: "Workforce", icon: ArrowLeftRight, roles: ["admin", "manager", "kitchen"] },
    { to: "/hr/vault", label: "Documents Vault", icon: FolderOpen, roles: ["admin"] },
  ]},
  { key: "ops", label: "Operations", items: [
    { to: "/materials", label: "Material Ledger", icon: Package, roles: ["admin", "manager"] },
    { to: "/kitchen", label: "Central Kitchen", icon: ChefHat, roles: ["admin", "kitchen"] },
    { to: "/tea", label: "Tea Business", icon: Coffee, roles: ["admin", "manager"], teaOnly: true },
  ]},
  { key: "admin", label: "Admin", items: [
    { to: "/manage", label: "Stores & Users", icon: Store, roles: ["admin"] },
    { to: "/activity", label: "Activity Log", icon: History, roles: ["admin"] },
    { to: "/backups", label: "Backups", icon: DatabaseBackup, roles: ["admin"] },
    { to: "/change-password", label: "Change Password", icon: KeyRound, roles: ["admin", "manager", "kitchen"] },
    { to: "/sessions", label: "Active Sessions", icon: ShieldCheck, roles: ["admin", "manager", "kitchen"] },
  ]},
];

function useCollapse(key, defaultOpen = true) {
  // NOTE: This localStorage only holds sidebar collapse booleans ("ssvp:sidebar:*"),
  // not tokens or PII. Auth tokens live in httpOnly cookies set by the backend.
  const storageKey = `ssvp:sidebar:${key}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v == null ? defaultOpen : v === "1";
    } catch (err) {
      // Incognito / quota-exceeded — fall back to default and don't spam the console
      if (typeof window !== "undefined" && !window.__ssvp_sidebar_ls_warn) {
        window.__ssvp_sidebar_ls_warn = true;
        console.warn("Sidebar collapse state disabled (localStorage unavailable):", err?.message || err);
      }
      return defaultOpen;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, open ? "1" : "0");
    } catch (err) {
      console.debug("useCollapse persist skipped:", err?.message || err);
    }
  }, [storageKey, open]);
  return [open, setOpen];
}

function NavItem({ item, onNavClick, returnedCount, pendingCount }) {
  const badgeValue = item.badge === "returned" ? returnedCount : item.badge === "pending" ? pendingCount : 0;
  const showBadge = badgeValue > 0;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onNavClick}
      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2 rounded-[10px] text-[13.5px] font-medium transition-colors duration-150 relative ${
          isActive
            ? "bg-[color:var(--turmeric)] text-[color:var(--navy-deep)] shadow-[0_0_16px_rgba(242,167,27,0.35)] font-semibold"
            : "text-[#C7D2E4] hover:bg-white/[.06] hover:text-white"
        }`
      }
    >
      <item.icon className="h-[17px] w-[17px] shrink-0" strokeWidth={1.9} />
      <span className="flex-1 truncate">{item.label}</span>
      {showBadge && (
        <span data-testid={`nav-badge-${item.badge}`}
          className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-[color:var(--tamarind)] text-white">
          {badgeValue}
        </span>
      )}
    </NavLink>
  );
}

function NavGroup({ group, filterFn, onNavClick, returnedCount, pendingCount }) {
  const items = group.items.filter(filterFn);
  const [open, setOpen] = useCollapse(group.key, true);
  if (!items.length) return null;
  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} data-testid={`nav-group-${group.key}`}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#7E90B0] hover:text-[#C7D2E4] transition-colors">
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} strokeWidth={2.5} />
        <span>{group.label}</span>
      </button>
      <div className={`overflow-hidden transition-all duration-200 ease-out ${open ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="space-y-0.5 pb-1.5">
          {items.map((it) => (
            <NavItem key={it.to} item={it} onNavClick={onNavClick} returnedCount={returnedCount} pendingCount={pendingCount} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SidebarContent({ filterFn, user, store, onNavClick, onLogout, returnedCount, pendingCount, storeLabel }) {
  return (
    <>
      <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/[.08]">
        <img src="/logo.webp" alt="SSVP" className="h-10 w-10 rounded-full object-cover flex-shrink-0 ring-1 ring-[color:var(--turmeric)]/30" />
        <div className="leading-tight">
          <div className="font-heading font-bold text-[15px] text-white">SSVP Account</div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--turmeric)] font-semibold">Pro · Cloud</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-2">
        {/* Pinned items */}
        <div className="space-y-0.5">
          {PINNED.filter(filterFn).map((it) => (
            <NavItem key={it.to} item={it} onNavClick={onNavClick} returnedCount={returnedCount} pendingCount={pendingCount} />
          ))}
        </div>
        {/* Grouped */}
        {GROUPS.map((g) => (
          <NavGroup key={g.key} group={g} filterFn={filterFn} onNavClick={onNavClick} returnedCount={returnedCount} pendingCount={pendingCount} />
        ))}
      </nav>
      <div className="p-3 border-t border-white/[.08]">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full bg-[color:var(--turmeric)] flex items-center justify-center font-bold text-[color:var(--navy-deep)] text-sm">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-white truncate">{user?.name}</div>
            <div className="text-[11px] text-[#8FA1BC] capitalize truncate">{user?.role} · {storeLabel}</div>
          </div>
          <button data-testid="logout-btn" onClick={onLogout} className="p-2 rounded-md hover:bg-white/[.08] text-[#8FA1BC]" title="Logout">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <button data-testid="theme-toggle" onClick={toggleTheme}
      title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      className="p-2 rounded-[10px] hover:bg-[color:var(--turmeric-soft)] text-[color:var(--ink-soft)] transition-colors duration-150">
      {theme === "dark" ? <Sun className="h-5 w-5" strokeWidth={1.9} /> : <Moon className="h-5 w-5" strokeWidth={1.9} />}
    </button>
  );
}

export default function Layout() {
  const { user, isAdmin, stores, selectedStoreId, setSelectedStoreId, store, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [returnedCount, setReturnedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const navigate = useNavigate();

  const filterFn = useCallback((n) => {
    if (!n.roles.includes(user?.role)) return false;
    if (n.teaOnly && !isAdmin && !store?.has_tea_vendor) return false;
    return true;
  }, [user, isAdmin, store]);

  const loadCounts = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/daily/returned-count");
      setReturnedCount(data?.count || 0);
    } catch (err) { console.debug("returned-count fetch failed:", err?.message || err); }
    if (user.role === "admin") {
      try {
        const { data } = await api.get("/daily", { params: { status: "pending" } });
        setPendingCount(Array.isArray(data) ? data.filter((r) => r.status === "pending" && !r.returned).length : 0);
      } catch (err) { console.debug("pending-count fetch failed:", err?.message || err); }
    }
  }, [user]);
  useEffect(() => {
    loadCounts();
    const id = setInterval(loadCounts, 60000);
    return () => clearInterval(id);
  }, [loadCounts]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const storeLabel = isAdmin ? (selectedStoreId === "all" ? "All Stores" : (stores.find((s) => s.id === selectedStoreId)?.name || "All Stores")) : (store?.name || "");

  const sidebar = (
    <SidebarContent filterFn={filterFn} user={user} store={store} returnedCount={returnedCount} pendingCount={pendingCount}
      storeLabel={storeLabel} onNavClick={() => setMobileOpen(false)} onLogout={handleLogout} />
  );

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      {/* Desktop sidebar — navy */}
      <aside className="hidden lg:flex w-64 fixed inset-y-0 left-0 z-50 flex-col bg-[color:var(--navy)] border-r border-black/20 no-print">
        {sidebar}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
          <aside className="fixed inset-y-0 left-0 w-64 z-50 flex flex-col bg-[color:var(--navy)] lg:hidden">
            {sidebar}
          </aside>
        </>
      )}

      <div id="app-shell" className="lg:pl-64 flex flex-col min-h-screen">
        <header className="h-16 sticky top-0 z-30 flex items-center gap-3 px-4 md:px-6 glass-header no-print">
          <button className="lg:hidden p-2 rounded-[10px] hover:bg-[color:var(--turmeric-soft)]" onClick={() => setMobileOpen(true)}>
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          {isAdmin && (
            <div className="w-48">
              <Select
                data-testid="store-filter"
                value={selectedStoreId}
                onChange={(e) => setSelectedStoreId(e.target.value)}
                className="h-9"
              >
                <option value="all">All Stores</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </Select>
            </div>
          )}
          <ThemeToggle />
          <NotificationBell />
        </header>

        <main className="flex-1 p-4 md:p-8 max-w-[1600px] mx-auto w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
